/* Presentation interactions only. Inquiry, payment and conversion handlers stay in their existing assets. */
(() => {
  const root = document.querySelector('.koval.kv4');
  if (!root) return;
  const initialized = new WeakSet();
  const scheduledImages = new WeakSet();
  const hydrateImage = (image) => {
    const src = image.dataset.kv4Src;
    if (!src) return;
    const srcset = image.dataset.kv4Srcset;
    // Keep the setting photo visible while its hover alternate downloads.
    if (image.dataset.kv4Image === 'hover') {
      image.setAttribute('data-kv4-image-pending', '');
      image.addEventListener('load', () => image.removeAttribute('data-kv4-image-pending'), { once: true });
    }
    image.loading = 'eager';
    if (srcset) image.setAttribute('srcset', srcset);
    image.src = src;
    delete image.dataset.kv4Src;
    delete image.dataset.kv4Srcset;
    delete image.dataset.kv4Image;
    imageObserver?.unobserve(image);
  };
  const imageObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) hydrateImage(entry.target); });
  }, { rootMargin: '350px 0px', threshold: 0 }) : null;
  const scheduleImages = (scope) => {
    scope.querySelectorAll('img[data-kv4-image]').forEach((image) => {
      if (scheduledImages.has(image)) return;
      scheduledImages.add(image);
      if (image.closest('[data-kv4-gallery-thumb]')) { hydrateImage(image); return; }
      if (image.dataset.kv4Image === 'hover') {
        const card = image.closest('.kv4-product-card-link') || image.parentElement;
        const reveal = () => hydrateImage(image);
        card.addEventListener('pointerenter', reveal, { once: true });
        card.addEventListener('focusin', reveal, { once: true });
        if (card.matches(':hover,:focus-within')) reveal();
      } else if (imageObserver) imageObserver.observe(image);
      else hydrateImage(image);
    });
  };
  const focusable = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex="0"]';
  let lockedMain = null;
  let mainWasInert = false;
  const syncMobile = (menu) => {
    const summary = menu.querySelector('summary');
    summary.setAttribute('aria-expanded', String(menu.open));
    summary.setAttribute('aria-label', menu.open ? 'Close Koval navigation' : 'Open Koval navigation');
    root.classList.toggle('kv4-menu-open', menu.open);
    if (menu.open) {
      root.querySelectorAll('[data-kv4-menu]').forEach((other) => { other.open = false; });
      const main = root.querySelector('main');
      if (main && !main.contains(menu)) {
        lockedMain = main;
        mainWasInert = main.inert;
        main.inert = true;
      }
    } else if (lockedMain) {
      lockedMain.inert = mainWasInert;
      lockedMain = null;
    }
  };
  const initStickyHeader = () => {
    const header = root.querySelector('[data-kv4-header]');
    if (!header || initialized.has(header)) return;
    initialized.add(header);
    let lastHeight = 0;
    const measure = () => {
      const height = Math.ceil(header.getBoundingClientRect().height);
      if (height && height !== lastHeight) {
        lastHeight = height;
        root.style.setProperty('--kv4-header-height', `${height}px`);
        root.dispatchEvent(new CustomEvent('kv4:header-resize', { detail: { height } }));
      }
    };
    const tint = () => header.classList.toggle('kv4-header--scrolled', window.scrollY > 8);
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(header, { box: 'border-box' });
    else window.addEventListener('resize', measure, { passive: true });
    window.addEventListener('scroll', tint, { passive: true });
    window.addEventListener('pageshow', () => { measure(); tint(); });
    measure();
    tint();
  };
  const initHomePinnedAction = () => {
    const bar = root.querySelector('.kv4-mobile-cta--home');
    const heroAction = root.querySelector('.kv4-home-headline .kv4-actions > a:first-child');
    if (!bar || !heroAction || initialized.has(heroAction)) return;
    initialized.add(heroAction);
    // Reveal only after the actual hero action has passed behind the persistent header.
    const headerBottom = () => Math.max(0, root.querySelector('[data-kv4-header]')?.getBoundingClientRect().bottom || 0);
    const update = () => bar.classList.toggle('kv4-mobile-cta--shown', heroAction.getBoundingClientRect().bottom <= headerBottom());
    let observer;
    const observe = () => {
      observer?.disconnect();
      observer = new IntersectionObserver(update, { rootMargin: `-${headerBottom()}px 0px 0px 0px`, threshold: 0 });
      observer.observe(heroAction);
      update();
    };
    if ('IntersectionObserver' in window) {
      observe();
      root.addEventListener('kv4:header-resize', observe);
    } else {
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }
    window.addEventListener('pageshow', update);
    update();
  };
  const init = (scope) => {
    scheduleImages(scope);
    initStickyHeader();
    initHomePinnedAction();
    scope.querySelectorAll('[data-koval-upload]').forEach((field) => {
      if (initialized.has(field)) return;
      const input = field.querySelector('input[type="file"]');
      if (!input) return;
      initialized.add(field);
      field.classList.add('kv4-upload');
      const face = document.createElement('div');
      face.className = 'kv4-upload-face';
      face.setAttribute('aria-hidden', 'true');
      face.innerHTML = '<svg width="36" height="36" viewBox="0 0 40 40" fill="none" stroke="currentColor" stroke-width="1.2"><rect x="4" y="8" width="32" height="24"/><circle cx="14" cy="17" r="3"/><path d="M4 28l10-8 7 6 5-4 10 8"/></svg><img class="kv4-upload-preview" alt="" hidden><span class="kv4-upload-copy"><span>Drop a photo here, or <u>choose a file</u></span><span class="kv4-upload-name">Up to 50 MB</span></span>';
      input.before(face);
      const preview = face.querySelector('img');
      const icon = face.querySelector('svg');
      const name = face.querySelector('.kv4-upload-name');
      let objectURL = null;
      const update = () => {
        if (objectURL) URL.revokeObjectURL(objectURL);
        objectURL = null;
        const file = input.files?.[0];
        name.textContent = file ? file.name : 'Up to 50 MB';
        preview.hidden = true;
        icon.removeAttribute('hidden');
        if (file && file.type.startsWith('image/')) {
          objectURL = URL.createObjectURL(file);
          preview.onload = () => { preview.hidden = false; icon.setAttribute('hidden', ''); };
          preview.onerror = () => { preview.hidden = true; icon.removeAttribute('hidden'); };
          preview.src = objectURL;
        }
      };
      input.addEventListener('change', update);
      update();
    });
    scope.querySelectorAll('[data-kv4-menu]').forEach((menu) => {
      if (initialized.has(menu)) return;
      initialized.add(menu);
      menu.addEventListener('toggle', () => {
        menu.querySelector('summary').setAttribute('aria-expanded', String(menu.open));
        if (menu.open) root.querySelectorAll('[data-kv4-menu]').forEach((other) => { if (other !== menu) other.open = false; });
      });
    });
    scope.querySelectorAll('[data-kv4-mobile-menu]').forEach((menu) => {
      if (initialized.has(menu)) return;
      initialized.add(menu);
      menu.addEventListener('toggle', () => syncMobile(menu));
      menu.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) menu.open = false;
      });
      menu.addEventListener('keydown', (event) => {
        if (!menu.open || event.key !== 'Tab') return;
        const items = [...menu.querySelectorAll(focusable)].filter((item) => item.getClientRects().length);
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      });
    });
    scope.querySelectorAll('[data-kv4-compare]').forEach((compare) => {
      if (initialized.has(compare)) return;
      initialized.add(compare);
      const range = compare.querySelector('[data-kv4-compare-range]');
      if (!range) return;
      const update = () => {
        const value = Math.max(0, Math.min(100, Number(range.value)));
        compare.style.setProperty('--kv4-position', `${value}%`);
        range.setAttribute('aria-valuetext', `${value} percent site photo`);
      };
      range.addEventListener('input', update);
      update();
    });
    scope.querySelectorAll('[data-kv4-gallery]').forEach((gallery) => {
      if (initialized.has(gallery)) return;
      initialized.add(gallery);
      const main = gallery.querySelector('[data-kv4-gallery-image]');
      const caption = gallery.querySelector('[data-kv4-gallery-caption]');
      const thumbs = [...gallery.querySelectorAll('[data-kv4-gallery-thumb]')];
      if (!main || !thumbs.length) return;
      const select = (thumb) => {
        const image = thumb.querySelector('img');
        if (image?.dataset.kv4Src) hydrateImage(image);
        const src = thumb.dataset.src || image?.getAttribute('src');
        if (!src) return;
        const srcset = thumb.dataset.srcset || image?.getAttribute('srcset');
        if (srcset) main.setAttribute('srcset', srcset);
        else main.removeAttribute('srcset');
        main.src = src;
        main.alt = thumb.dataset.alt || image?.alt || main.alt;
        main.style.objectFit = thumb.dataset.fit || 'cover';
        if (caption && thumb.dataset.caption) caption.textContent = thumb.dataset.caption;
        thumbs.forEach((item) => item.setAttribute('aria-pressed', String(item === thumb)));
      };
      thumbs.forEach((thumb, index) => {
        if (!thumb.hasAttribute('aria-pressed')) thumb.setAttribute('aria-pressed', String(index === 0));
        thumb.addEventListener('click', () => select(thumb));
        thumb.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = thumbs[(index + 1) % thumbs.length];
          if (event.key === 'ArrowLeft') next = thumbs[(index + thumbs.length - 1) % thumbs.length];
          if (event.key === 'Home') next = thumbs[0];
          if (event.key === 'End') next = thumbs[thumbs.length - 1];
          if (next) { event.preventDefault(); next.focus(); select(next); }
        });
      });
    });
  };
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-kv4-header]')) root.querySelectorAll('[data-kv4-menu]').forEach((menu) => { menu.open = false; });
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const menus = [...root.querySelectorAll('[data-kv4-menu][open],[data-kv4-mobile-menu][open]')];
    menus.forEach((menu) => { menu.open = false; menu.querySelector('summary')?.focus(); });
  });
  window.matchMedia('(min-width: 1181px)').addEventListener('change', (event) => {
    if (event.matches) root.querySelectorAll('[data-kv4-mobile-menu]').forEach((menu) => { menu.open = false; });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  init(root);
})();
